import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { connectAndMint } from "@/lib/caryConnect";
import { X, FolderSync, Loader2, KeyRound, Wallet, ShieldCheck, Zap, Layers, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";

const LS_KEY = "cp_live_token";

export default function CaryPactLiveSyncModal({ node, onClose, onSynced, chartRoot = false }) {
  const [mode, setMode] = useState("full"); // full | quick
  const [step, setStep] = useState("input"); // input | preview | job
  const [token, setToken] = useState(() => { try { return localStorage.getItem(LS_KEY) || ""; } catch { return ""; } });
  const lockedAddress = node.wallet_name && /^0x[a-fA-F0-9]{40}$/.test(node.wallet_name);
  const [address, setAddress] = useState(lockedAddress ? node.wallet_name : "");
  const [depth, setDepth] = useState(3);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // quick-mode preview
  const [preview, setPreview] = useState(null);
  const [selNew, setSelNew] = useState(new Set());
  const [selUpd, setSelUpd] = useState(new Set());

  // full-mode job
  const [job, setJob] = useState(null);
  const [resumeToken, setResumeToken] = useState("");
  const pollRef = useRef(null);

  const rememberToken = (t) => { try { remember ? localStorage.setItem(LS_KEY, t) : localStorage.removeItem(LS_KEY); } catch { /* private mode */ } };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ---------- FULL background sync ----------
  const startFull = async (overrideToken) => {
    const tok = (overrideToken ?? token).trim();
    if (!address.trim()) { toast.error("This tile has no wallet address."); return; }
    setBusy(true);
    try {
      const body = tok ? { token: tok, address: address.trim() } : { address: address.trim() };
      const { data } = chartRoot
        ? await api.post(`/carypact-live/sync-all`, body)
        : await api.post(`/org-nodes/${node.id}/carypact-sync-job/start`, body);
      if (tok) rememberToken(tok);
      setStep("job");
      poll(data.job_id);
    } catch (err) {
      if (err.response?.status === 409 || err.response?.data?.detail === "needs_token") {
        toast.error("No valid CaryPact token yet — tap “Connect wallet” (no F12) or paste a token below.", { duration: 7000 });
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not start the sync.");
      }
    } finally { setBusy(false); }
  };

  // One-tap: connect wallet → mint token server-side → immediately sync this branch.
  const connectAndSync = async () => {
    setConnecting(true);
    try {
      const res = await connectAndMint(node.name);
      toast.success(`Wallet connected (${res.address.slice(0, 6)}…${res.address.slice(-4)}) — syncing now.`, { duration: 5000 });
      await startFull(""); // token now stored server-side; sync reuses it
    } catch (err) {
      toast.error(err.message || "Could not connect the wallet.", { duration: 8000 });
    } finally { setConnecting(false); }
  };

  const poll = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const { data } = await api.get(`/carypact-sync-job/${jobId}`);
        setJob(data);
        if (["completed", "failed", "canceled", "paused_token"].includes(data.status)) {
          clearInterval(pollRef.current); pollRef.current = null;
          if (data.status === "completed") onSynced?.();
        }
      } catch { /* transient */ }
    };
    tick();
    pollRef.current = setInterval(tick, 2000);
  };

  const resumeFull = async () => {
    if (!resumeToken.trim()) { toast.error("Paste a fresh token to resume."); return; }
    setBusy(true);
    try {
      await api.post(`/carypact-sync-job/${job.id}/resume`, { token: resumeToken.trim() });
      rememberToken(resumeToken.trim());
      setResumeToken("");
      poll(job.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not resume.");
    } finally { setBusy(false); }
  };

  const cancelFull = async () => {
    try { await api.post(`/carypact-sync-job/${job.id}/cancel`); } catch { /* ignore */ }
    if (pollRef.current) clearInterval(pollRef.current);
    setJob((j) => ({ ...j, status: "canceled", phase: "Canceled." }));
  };

  // ---------- QUICK (bounded depth) sync ----------
  const fetchQuick = async () => {
    if (!token.trim() || !address.trim()) { toast.error("Paste your CaryPact token and wallet address."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/org-nodes/${node.id}/carypact-live/fetch`, { token: token.trim(), address: address.trim(), depth });
      rememberToken(token.trim());
      setPreview(data);
      setSelNew(new Set((data.new || []).map((_, i) => i)));
      setSelUpd(new Set((data.updates || []).map((_, i) => i)));
      setStep("preview");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not reach CaryPact.");
    } finally { setBusy(false); }
  };

  const applyQuick = async () => {
    const create = (preview.new || []).filter((_, i) => selNew.has(i));
    const update = (preview.updates || []).filter((_, i) => selUpd.has(i)).map((u) => ({
      node_id: u.node_id, fields: Object.fromEntries(Object.entries(u.changes).map(([k, v]) => [k, v.to])),
    }));
    if (!create.length && !update.length) { toast.info("Nothing selected."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/org-nodes/${node.id}/carypact-live/apply`, { create, update });
      toast.success(`Synced: ${data.created} added, ${data.updated} updated. Snapshot saved first.`, { duration: 8000 });
      onSynced?.(); onClose?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Sync failed.");
    } finally { setBusy(false); }
  };

  const toggle = (set, setter, i) => { const n = new Set(set); n.has(i) ? n.delete(i) : n.add(i); setter(n); };
  const fmt = (v) => (v == null || v === "" ? "—" : typeof v === "number" ? v.toLocaleString() : String(v));

  return createPortal(
    <div className="fixed inset-0 z-[210] flex items-center justify-center p-4" data-testid="cp-live-modal">
      <div className="absolute inset-0" style={{ background: "rgba(15,18,38,0.40)", backdropFilter: "blur(4px)" }} onClick={onClose} />
      <div className="relative glass w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto" style={{ borderRadius: 20 }}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="kpi-label flex items-center gap-1.5"><FolderSync size={12} /> CaryPact sync · {node.name}</div>
            <h3 className="font-display text-lg font-semibold tracking-tight">
              {step === "job" ? "Syncing your whole tree" : step === "preview" ? "Review before applying" : "Sync your downline"}
            </h3>
          </div>
          <button onClick={onClose} className="org-icon-btn" title="Close" data-testid="cp-live-close"><X size={16} /></button>
        </div>

        {step === "input" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2" data-testid="cp-mode-toggle" style={{ display: chartRoot ? "none" : "grid" }}>
              <button onClick={() => setMode("full")} className="p-3 rounded-xl text-left transition-colors" data-testid="cp-mode-full"
                style={{ background: mode === "full" ? "rgba(124,58,237,0.10)" : "rgba(15,18,38,0.03)", border: `1px solid ${mode === "full" ? "rgba(124,58,237,0.4)" : "rgba(15,18,38,0.08)"}` }}>
                <div className="flex items-center gap-1.5 font-semibold text-sm"><Zap size={14} style={{ color: "#7C3AED" }} /> Entire tree</div>
                <div className="text-[11px] text-ink-3 mt-0.5">Every level, runs in the background. Best for 100s–1000s.</div>
              </button>
              <button onClick={() => setMode("quick")} className="p-3 rounded-xl text-left transition-colors" data-testid="cp-mode-quick"
                style={{ background: mode === "quick" ? "rgba(64,96,255,0.10)" : "rgba(15,18,38,0.03)", border: `1px solid ${mode === "quick" ? "rgba(64,96,255,0.4)" : "rgba(15,18,38,0.08)"}` }}>
                <div className="flex items-center gap-1.5 font-semibold text-sm"><Layers size={14} style={{ color: "#4060FF" }} /> Quick</div>
                <div className="text-[11px] text-ink-3 mt-0.5">A few levels, instant preview to tick/untick.</div>
              </button>
            </div>

            <label className="block">
              <span className="kpi-label flex items-center gap-1.5 mb-1"><Wallet size={12} /> {chartRoot ? "Your wallet address" : "This tile's wallet"}</span>
              <input value={address} onChange={(e) => setAddress(e.target.value)} readOnly={lockedAddress}
                placeholder="0x…" className="input-glass w-full text-sm font-mono" data-testid="cp-live-address"
                style={lockedAddress ? { opacity: 0.75, cursor: "not-allowed" } : undefined} />
              {lockedAddress && <span className="text-[11px] text-ink-3 mt-1 block">Locked to this tile's wallet — syncs this wallet and everyone below it.</span>}
            </label>

            {mode === "full" && (
              <button onClick={connectAndSync} disabled={connecting || busy} className="btn-primary w-full" style={{ background: (connecting || busy) ? undefined : "#7C3AED" }} data-testid="cp-connect-sync-btn">
                {connecting ? <><Loader2 size={14} className="animate-spin" /> Waiting for your wallet…</> : <><Zap size={14} /> Connect wallet &amp; sync (no F12)</>}
              </button>
            )}

            <details className="rounded-lg" data-testid="cp-token-advanced">
              <summary className="cursor-pointer text-[12px] font-semibold text-ink-2 flex items-center gap-1.5"><KeyRound size={12} /> Or paste a token manually (advanced)</summary>
              <div className="mt-2 space-y-2">
                <input value={token} onChange={(e) => setToken(e.target.value)} placeholder="x-auth-token" className="input-glass w-full text-sm font-mono" data-testid="cp-live-token" type="password" />
                <div className="text-[11px] text-ink-3">F12 → Network → any <b>/api/</b> request → copy the <b>x-auth-token</b> header. A temporary key, never your seed phrase.</div>
              </div>
            </details>

            <div className="flex items-center gap-3 flex-wrap">
              {mode === "quick" && (
                <label className="flex items-center gap-1.5 text-xs text-ink-2">Levels deep
                  <select value={depth} onChange={(e) => setDepth(Number(e.target.value))} className="input-glass text-sm !py-1 !px-2" data-testid="cp-live-depth">
                    {[1, 2, 3, 4, 5].map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </label>
              )}
              <label className="flex items-center gap-1.5 text-xs text-ink-2 cursor-pointer">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="cp-live-remember" /> Remember token on this device
              </label>
            </div>

            {mode === "full" ? (
              <button onClick={() => startFull()} disabled={busy} className="btn-secondary w-full text-sm" data-testid="cp-full-start-btn">
                {busy ? <><Loader2 size={14} className="animate-spin" /> Starting…</> : <><FolderSync size={14} /> Sync with saved token</>}
              </button>
            ) : (
              <button onClick={fetchQuick} disabled={busy} className="btn-primary w-full" data-testid="cp-live-fetch-btn">
                {busy ? <><Loader2 size={14} className="animate-spin" /> Pulling…</> : <><FolderSync size={14} /> Fetch & preview</>}
              </button>
            )}
          </div>
        )}

        {step === "job" && job && (
          <div className="space-y-4" data-testid="cp-job-panel">
            <div className="flex items-center gap-2.5">
              {job.status === "running" && <Loader2 size={18} className="animate-spin" style={{ color: "#7C3AED" }} />}
              {job.status === "completed" && <CheckCircle2 size={18} style={{ color: "#00863A" }} />}
              {job.status === "paused_token" && <AlertTriangle size={18} style={{ color: "#F59E0B" }} />}
              {(job.status === "failed" || job.status === "canceled") && <XCircle size={18} style={{ color: "#DC2626" }} />}
              <div className="text-sm text-ink-2 flex-1" data-testid="cp-job-phase">{job.phase || job.status}</div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-3 rounded-xl" style={{ background: "rgba(15,18,38,0.03)" }}>
                <div className="font-display text-2xl font-semibold" data-testid="cp-job-fetched">{job.fetched ?? 0}</div>
                <div className="text-[11px] text-ink-3">people walked</div>
              </div>
              <div className="p-3 rounded-xl" style={{ background: "rgba(0,134,58,0.06)" }}>
                <div className="font-display text-2xl font-semibold" style={{ color: "#00863A" }} data-testid="cp-job-created">{job.created ?? 0}</div>
                <div className="text-[11px] text-ink-3">added</div>
              </div>
              <div className="p-3 rounded-xl" style={{ background: "rgba(64,96,255,0.06)" }}>
                <div className="font-display text-2xl font-semibold" style={{ color: "#4060FF" }} data-testid="cp-job-updated">{job.updated ?? 0}</div>
                <div className="text-[11px] text-ink-3">updated</div>
              </div>
            </div>

            {job.status === "running" && (
              <>
                {job.queued ? <div className="text-[11px] text-ink-3 text-center">{job.queued} more in the queue…</div> : null}
                <button onClick={cancelFull} className="btn-secondary w-full text-sm" data-testid="cp-job-cancel-btn">Stop sync</button>
              </>
            )}

            {job.status === "paused_token" && (
              <div className="space-y-2 rounded-xl p-3" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)" }}>
                <div className="text-xs text-ink-2">Your token expired mid-sync — progress is saved. Paste a fresh <b>x-auth-token</b> to pick up exactly where it left off.</div>
                <input value={resumeToken} onChange={(e) => setResumeToken(e.target.value)} placeholder="fresh x-auth-token" className="input-glass w-full text-sm font-mono" type="password" data-testid="cp-job-resume-token" />
                <div className="flex gap-2">
                  <button onClick={resumeFull} disabled={busy} className="btn-primary flex-1 text-sm" data-testid="cp-job-resume-btn">{busy ? "Resuming…" : "Resume sync"}</button>
                  <button onClick={cancelFull} className="btn-secondary text-sm" data-testid="cp-job-cancel-btn">Stop</button>
                </div>
              </div>
            )}

            {job.status === "completed" && (
              <div className="rounded-xl p-3 text-sm" style={{ background: "rgba(0,134,58,0.06)", border: "1px solid rgba(0,134,58,0.2)" }} data-testid="cp-job-done">
                <div className="font-semibold text-ink flex items-center gap-1.5"><ShieldCheck size={14} style={{ color: "#00863A" }} /> Whole tree synced.</div>
                <div className="text-ink-2 text-xs mt-1">{job.created} added, {job.updated} updated across {job.fetched} people. A snapshot was saved first — restore from Snapshots if anything looks off.{job.summary?.capped ? " (Reached the safety cap — run again to continue.)" : ""}</div>
                <button onClick={onClose} className="btn-primary w-full text-sm mt-2" data-testid="cp-job-close-btn">Done</button>
              </div>
            )}

            {(job.status === "failed" || job.status === "canceled") && (
              <div className="rounded-xl p-3 text-sm" style={{ background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)" }}>
                <div className="text-ink-2 text-xs">{job.error || "Sync stopped."} {job.created ? `${job.created} tiles were already added and kept.` : ""}</div>
                <button onClick={() => { setStep("input"); setJob(null); }} className="btn-secondary w-full text-sm mt-2" data-testid="cp-job-retry-btn">Back</button>
              </div>
            )}
          </div>
        )}

        {step === "preview" && preview && (
          <div className="space-y-4">
            <div className="text-xs text-ink-2">Pulled <b>{preview.fetched_count}</b> people ({preview.depth} levels). Untick anything, then apply. Snapshot saved first.</div>
            <div>
              <div className="kpi-label mb-2">New ({(preview.new || []).length})</div>
              {(preview.new || []).length === 0 ? <div className="text-xs text-ink-3">No new people.</div> : (preview.new || []).map((r, i) => (
                <label key={i} className="flex items-center gap-2.5 p-2.5 rounded-lg mb-1.5 cursor-pointer" style={{ background: "rgba(0,134,58,0.06)", border: "1px solid rgba(0,134,58,0.15)" }} data-testid={`cp-live-new-${i}`}>
                  <input type="checkbox" checked={selNew.has(i)} onChange={() => toggle(selNew, setSelNew, i)} />
                  <div className="flex-1 min-w-0 text-sm leading-tight">
                    <div className="font-medium truncate">{r.name} <span className="text-[10px] text-ink-3">· L{r.depth}</span></div>
                    <div className="text-[11px] text-ink-3 font-mono truncate">{[r.rank, r.principal != null ? `$${fmt(r.principal)}` : null].filter(Boolean).join(" · ") || "no extra details"}</div>
                  </div>
                </label>
              ))}
            </div>
            <div>
              <div className="kpi-label mb-2">Updates ({(preview.updates || []).length})</div>
              {(preview.updates || []).length === 0 ? <div className="text-xs text-ink-3">No changes.</div> : (preview.updates || []).map((u, i) => (
                <label key={u.node_id} className="flex items-start gap-2.5 p-2.5 rounded-lg mb-1.5 cursor-pointer" style={{ background: "rgba(64,96,255,0.06)", border: "1px solid rgba(64,96,255,0.15)" }} data-testid={`cp-live-upd-${i}`}>
                  <input type="checkbox" checked={selUpd.has(i)} onChange={() => toggle(selUpd, setSelUpd, i)} className="mt-0.5" />
                  <div className="flex-1 min-w-0 text-sm leading-tight">
                    <div className="font-medium truncate">{u.name}</div>
                    {Object.entries(u.changes).map(([k, v]) => <div key={k} className="text-[11px] text-ink-3 font-mono">{k}: {fmt(v.from)} → <b className="text-ink">{fmt(v.to)}</b></div>)}
                  </div>
                </label>
              ))}
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={applyQuick} disabled={busy} className="btn-primary flex-1" data-testid="cp-live-apply-btn">{busy ? <><Loader2 size={14} className="animate-spin" /> Applying…</> : <><ShieldCheck size={14} /> Apply sync</>}</button>
              <button onClick={() => { setStep("input"); setPreview(null); }} className="btn-secondary" data-testid="cp-live-back-btn">Back</button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
